import { AxiosResponse } from 'axios';
import { AddressBookItem } from '@/features/shipment/types';
import { GlobalResponseType, UrlQuery } from '@/features/common/types';
import { useAxios } from '@/features/common/helpers/axios';
import { parseError } from '@/features/common/helpers/errorParser';
import i18n from '@/i18n';

export const { axiosService, baseURL } = useAxios();

const filterExceptionMessage = (err: GlobalResponseType): GlobalResponseType => {
  if (!err.errorType) return err;

  const messageKey = `shipment.errorMessages.${err.errorType}`;
  if (!i18n.te(messageKey)) return err;

  return {
    ...err,
    errorMessage: i18n.t(messageKey) as string,
  };
};

export const getAddressesBook = async ({
  offset = 0,
  limit = 20,
  orderBy = 'created',
  orderByDesc = true,
  search = null,
}: UrlQuery = {}): Promise<AddressBookItem[]> => {
  try {
    let queryString = `?Offset=${offset}&Limit=${limit}&OrderBy=${orderBy}&OrderByDescending=${orderByDesc}`;
    if (search) {
      queryString += `&search=${encodeURI(search)}`;
    }

    const addressesBookRes: AxiosResponse = await axiosService.get(`/account/address-book${queryString}`);
    return addressesBookRes.data ?? [];
  } catch (err) {
    return [];
  }
};

export const getAddressesBookByCountry = async ({
  offset = 0,
  limit = 20,
  orderBy = 'created',
  orderByDesc = true,
  search = null,
  countryCode,
}: UrlQuery = {}): Promise<AddressBookItem[]> => {
  try {
    let queryString = `?Offset=${offset}&Limit=${limit}&OrderBy=${orderBy}&OrderByDescending=${orderByDesc}`;
    if (search) {
      queryString += `&search=${encodeURI(search)}`;
    }
    if (countryCode) {
      queryString += `&countryCode=${encodeURI(countryCode)}`;
    }

    const addressesBookRes: AxiosResponse = await axiosService.get(`/address-book${queryString}`);
    return addressesBookRes.data ?? [];
  } catch (err) {
    return [];
  }
};

export const createAddressBookItem = async (
  addressBookItem: AddressBookItem,
): Promise<{
  item?: AddressBookItem
} & GlobalResponseType> => {
  const { countryCode } = addressBookItem;

  if (!countryCode) {
    return {
      successful: false,
      errorMessage: i18n.t('shipment.whereShipment.errorMessages.CountryCodeMissingError') as string,
      redirectUrl: '',
    };
  }

  if (countryCode.length !== 2) {
    return {
      successful: false,
      errorMessage: i18n.t('shipment.whereShipment.errorMessages.CountryCodeValidationError') as string,
      redirectUrl: '',
    };
  }

  try {
    const addressBookRes: AxiosResponse = await axiosService.post('/account/address-book', addressBookItem);
    return {
      successful: true,
      item: addressBookRes.data,
      redirectUrl: '',
      errorMessage: '',
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const updateAddressBookItem = async (
  id: number,
  addressBookItem: AddressBookItem,
): Promise<{
  item?: AddressBookItem
} & GlobalResponseType> => {
  const { countryCode } = addressBookItem;

  if (!countryCode) {
    return {
      successful: false,
      errorMessage: i18n.t('shipment.whereShipment.errorMessages.CountryCodeMissingError') as string,
      redirectUrl: '',
    };
  }

  if (countryCode.length !== 2) {
    return {
      successful: false,
      errorMessage: i18n.t('shipment.whereShipment.errorMessages.CountryCodeValidationError') as string,
      redirectUrl: '',
    };
  }

  try {
    const addressBookRes: AxiosResponse = await axiosService.put(`/account/address-book/${id}`, addressBookItem);
    return {
      successful: true,
      item: addressBookRes.data,
      redirectUrl: '',
      errorMessage: '',
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const deleteAddressBookItem = async (id: number): Promise<boolean> => {
  try {
    await axiosService.delete(`/account/address-book/${id}`);
    return true;
  } catch (err) {
    return false;
  }
};
